<script setup>
// Auditoria → Relatório da IA: a lista dos relatórios (semanal, mensal ou
// gerado na hora) e o escolhido: números do período com o anterior, resumo,
// assuntos e motivos agrupados pela IA, destaques por seção e recomendações,
// cada um com as conversas que o sustentam.
//
// Com plano, o "Gerar agora" usa a cota do ciclo; quando ela acaba, o popup
// oferece o relatório extra pelo preço do plano (só o administrador compra).
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { createGestaoApi, nota, reais } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const { t } = useI18n({ messages });
const api = computed(() => createGestaoApi(props.accountId));

const lista = ref([]);
const carregando = ref(true);
const erro = ref('');
const escolhido = ref(null);
const relatorio = ref(null);
const pedindo = ref(false);
const destinatarios = ref([]);
const enviando = ref(false);
const envioAberto = ref(false);
const cota = ref(null);
// O pedido que esbarrou na cota: { dias, inclusos, preco_extra, fecha_em, administrador }.
const compra = ref(null);
const escolhidos = ref([]);
const outros = ref('');
const aviso = ref('');
const baixando = ref(false);
let relogio = null;

const data = iso =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
// O fim é exclusivo (meia-noite do dia seguinte): mostra o último dia do período.
const periodo = item =>
  t('NEUXS_GESTAO.RELATORIO.PERIODO', {
    de: data(item.inicio),
    ate: data(new Date(new Date(item.fim).getTime() - 1000).toISOString()),
  });

const abre = async id => {
  escolhido.value = id;
  try {
    const { data: dados } = await api.value.relatorio(id);
    if (escolhido.value === id) relatorio.value = dados;
  } catch {
    relatorio.value = null;
  }
};

const carregaLista = async () => {
  try {
    const { data: dados } = await api.value.relatorios();
    lista.value = dados.relatorios;
    destinatarios.value = dados.destinatarios || [];
    cota.value = dados.cota || null;
    if (!escolhido.value && lista.value.length) abre(lista.value[0].id);
  } catch {
    erro.value = t('NEUXS_GESTAO.ERRO');
  } finally {
    carregando.value = false;
  }
};

// Enquanto algum relatório está sendo gerado, confere de tempos em tempos.
const confere = async () => {
  if (!lista.value.some(item => item.status === 'gerando')) return;
  await carregaLista();
  if (relatorio.value?.status === 'gerando') abre(escolhido.value);
};

const gera = async (dias, comprar = false) => {
  pedindo.value = true;
  erro.value = '';
  try {
    const { data: novo } = await api.value.geraRelatorio(dias, comprar);
    compra.value = null;
    lista.value = [novo, ...lista.value];
    abre(novo.id);
    carregaLista();
  } catch (e) {
    if (e.response?.status === 402 && e.response.data?.cota_esgotada) {
      compra.value = { dias, ...e.response.data };
    } else {
      compra.value = null;
      erro.value = e.response?.data?.erro || t('NEUXS_GESTAO.ERRO');
    }
  } finally {
    pedindo.value = false;
  }
};
const dataCurta = dia => (dia ? dia.split('-').reverse().join('/') : '—');

onMounted(() => {
  carregaLista();
  relogio = setInterval(confere, 5000);
});
onBeforeUnmount(() => clearInterval(relogio));

const atual = computed(() => relatorio.value?.numeros?.atual || {});
const anterior = computed(() => relatorio.value?.numeros?.anterior || {});
const porcento = valor => (valor === null || valor === undefined ? '—' : `${valor}%`);
const NUMEROS = [
  { chave: 'conversas', valor: n => n.conversas?.toLocaleString('pt-BR') ?? '—' },
  { chave: 'ia_sem_humano', valor: n => porcento(n.ia_sem_humano) },
  { chave: 'csat', valor: n => nota(n.csat?.media) },
  { chave: 'nota', valor: n => nota(n.equipe?.nota_media) },
  { chave: 'retencao', valor: n => porcento(n.retencao?.taxa_de_retencao) },
  { chave: 'oficiais', valor: n => n.mensagens_oficiais?.total?.toLocaleString('pt-BR') ?? '—' },
];
const assuntos = computed(() => relatorio.value?.categorias?.assuntos || []);
const motivos = computed(() => relatorio.value?.categorias?.motivos || []);
const maior = itens => Math.max(1, ...itens.map(item => item.total));
const SECOES = ['assuntos', 'cancelamentos', 'suporte', 'equipe', 'ia', 'custos'];
const destaques = computed(() =>
  SECOES.map(secao => ({
    secao,
    itens: (relatorio.value?.texto?.destaques || []).filter(item => item.secao === secao),
  })).filter(grupo => grupo.itens.length)
);
const abreEnvio = () => {
  escolhidos.value = destinatarios.value.map(pessoa => pessoa.id);
  outros.value = '';
  aviso.value = '';
  envioAberto.value = !envioAberto.value;
};

const envia = async () => {
  enviando.value = true;
  aviso.value = '';
  try {
    const { data: resposta } = await api.value.enviaRelatorio(relatorio.value.id, {
      user_ids: escolhidos.value,
      emails: outros.value,
    });
    aviso.value = t('NEUXS_GESTAO.RELATORIO.ENVIADO', { n: resposta.enviados });
    envioAberto.value = false;
  } catch (e) {
    aviso.value = e.response?.data?.erro || t('NEUXS_GESTAO.ERRO');
  } finally {
    enviando.value = false;
  }
};

// O PDF vem pela API (com a sessão de quem está logado) e é salvo pelo navegador.
const baixa = async () => {
  baixando.value = true;
  aviso.value = '';
  try {
    const resposta = await api.value.pdfDoRelatorio(relatorio.value.id);
    const nome =
      /filename="?([^";]+)/.exec(resposta.headers['content-disposition'] || '')?.[1] ||
      'relatorio.pdf';
    const link = document.createElement('a');
    link.href = URL.createObjectURL(resposta.data);
    link.download = nome;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 10000);
  } catch {
    aviso.value = t('NEUXS_GESTAO.RELATORIO.ERRO_PDF');
  } finally {
    baixando.value = false;
  }
};

const linkDaConversa = id => frontendURL(`accounts/${props.accountId}/conversations/${id}`);
</script>

<template>
  <div class="grid items-start grid-cols-1 gap-5 lg:grid-cols-[16rem_minmax(0,1fr)]">
    <aside class="flex flex-col gap-3">
      <h2 class="m-0 text-heading-3 text-n-slate-12">{{ t('NEUXS_GESTAO.RELATORIO.LISTA') }}</h2>
      <div class="flex flex-col gap-2">
        <Button size="sm" :label="t('NEUXS_GESTAO.RELATORIO.GERAR_7')" :is-loading="pedindo" @click="gera(7)" />
        <Button size="sm" variant="faded" :label="t('NEUXS_GESTAO.RELATORIO.GERAR_30')" :disabled="pedindo" @click="gera(30)" />
      </div>
      <p v-if="erro" class="m-0 text-xs text-n-ruby-11">{{ erro }}</p>
      <p v-if="cota" class="m-0 text-xs text-n-slate-11">
        {{ t('NEUXS_GESTAO.RELATORIO.COTA', { inclusos: cota.inclusos, restantes: cota.restantes, fecha: dataCurta(cota.fecha_em) }) }}
      </p>
      <p v-else class="m-0 text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.RELATORIO.AGENDA') }}</p>
      <div v-if="carregando" class="flex justify-center py-6"><Spinner /></div>
      <p v-else-if="!lista.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.RELATORIO.VAZIO') }}</p>
      <ul v-else class="flex flex-col gap-1.5 p-0 m-0 list-none">
        <li v-for="item in lista" :key="item.id">
          <button
            type="button"
            class="flex flex-col w-full gap-0.5 px-3 py-2 text-start rounded-lg border"
            :class="escolhido === item.id ? 'border-n-blue-9 bg-n-alpha-1' : 'border-transparent bg-n-alpha-1 hover:bg-n-alpha-2'"
            @click="abre(item.id)"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ t(`NEUXS_GESTAO.RELATORIO.TIPOS.${item.tipo}`) }}
              <span v-if="item.extra" class="text-xs font-normal text-n-amber-11">· {{ t('NEUXS_GESTAO.RELATORIO.EXTRA') }}</span>
            </span>
            <span class="text-xs text-n-slate-11">{{ periodo(item) }}</span>
            <span
              v-if="item.status !== 'pronto'"
              class="text-xs"
              :class="item.status === 'falhou' ? 'text-n-ruby-11' : 'text-n-amber-11'"
            >
              {{ t(`NEUXS_GESTAO.RELATORIO.STATUS.${item.status}`) }}
            </span>
          </button>
        </li>
      </ul>
    </aside>

    <article class="p-5 border rounded-xl border-n-weak">
      <p v-if="!relatorio" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.RELATORIO.ESCOLHA') }}</p>
      <template v-else>
        <header class="flex flex-wrap items-start gap-3 pb-3 mb-4 border-b border-n-weak">
          <div class="flex-1 min-w-0">
            <p class="m-0 text-xs uppercase tracking-wide text-n-slate-10">
              {{ t(`NEUXS_GESTAO.RELATORIO.TIPOS.${relatorio.tipo}`) }}
            </p>
            <h2 class="m-0 mt-1 text-heading-2 text-n-slate-12">{{ periodo(relatorio) }}</h2>
          </div>
          <div v-if="relatorio.status === 'pronto'" class="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="faded"
              color="slate"
              icon="i-lucide-file-down"
              :label="t('NEUXS_GESTAO.RELATORIO.BAIXAR')"
              :is-loading="baixando"
              @click="baixa"
            />
            <Button
              size="sm"
              variant="faded"
              icon="i-lucide-mail"
              :label="t('NEUXS_GESTAO.RELATORIO.ENVIAR')"
              @click="abreEnvio"
            />
          </div>
        </header>

        <p v-if="aviso" class="p-2 m-0 mb-4 text-sm rounded-lg bg-n-alpha-2 text-n-slate-12">{{ aviso }}</p>

        <form
          v-if="envioAberto && relatorio.status === 'pronto'"
          class="flex flex-col gap-3 p-4 mb-5 border rounded-xl border-n-weak"
          @submit.prevent="envia"
        >
          <p class="m-0 text-sm font-medium text-n-slate-12">{{ t('NEUXS_GESTAO.RELATORIO.ENVIO_TITULO') }}</p>
          <p class="m-0 text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.RELATORIO.ENVIO_DICA') }}</p>
          <p class="m-0 text-xs uppercase tracking-wide text-n-slate-10">{{ t('NEUXS_GESTAO.RELATORIO.COM_ACESSO') }}</p>
          <p v-if="!destinatarios.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.RELATORIO.SEM_ACESSO') }}</p>
          <label v-for="pessoa in destinatarios" :key="pessoa.id" class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="escolhidos" type="checkbox" :value="pessoa.id" class="!mb-0" />
            {{ pessoa.nome }} <span class="text-xs text-n-slate-10">{{ pessoa.email }}</span>
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('NEUXS_GESTAO.RELATORIO.OUTROS') }}
            <input v-model="outros" type="text" class="!mb-0" />
          </label>
          <div>
            <Button type="submit" size="sm" :label="t('NEUXS_GESTAO.RELATORIO.MANDAR')" :is-loading="enviando" />
          </div>
        </form>

        <div v-if="relatorio.status === 'gerando'" class="flex items-center gap-3 text-sm text-n-slate-11">
          <Spinner /> {{ t('NEUXS_GESTAO.RELATORIO.GERANDO') }}
        </div>
        <p v-else-if="relatorio.status === 'falhou'" class="m-0 text-sm text-n-ruby-11">
          {{ t('NEUXS_GESTAO.RELATORIO.FALHOU', { erro: relatorio.erro }) }}
        </p>

        <div v-else class="flex flex-col gap-6">
          <section class="flex flex-col gap-3">
            <h3 class="m-0 text-heading-3 text-n-blue-11">{{ t('NEUXS_GESTAO.RELATORIO.RESUMO') }}</h3>
            <p class="m-0 text-sm leading-6 text-n-slate-12 max-w-3xl">{{ relatorio.texto.resumo }}</p>
            <div class="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
              <div v-for="numero in NUMEROS" :key="numero.chave" class="px-3 py-2 rounded-lg bg-n-alpha-2">
                <span class="block text-xs text-n-slate-11">{{ t(`NEUXS_GESTAO.RELATORIO.NUMEROS.${numero.chave}`) }}</span>
                <strong class="block text-lg font-medium tabular-nums text-n-slate-12">{{ numero.valor(atual) }}</strong>
                <span class="text-xs text-n-slate-10">
                  {{ t('NEUXS_GESTAO.RELATORIO.ANTERIOR', { valor: numero.valor(anterior) }) }}
                </span>
              </div>
            </div>
          </section>

          <section v-if="assuntos.length || motivos.length" class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div v-if="assuntos.length" class="flex flex-col gap-2">
              <h3 class="m-0 text-heading-3 text-n-blue-11">{{ t('NEUXS_GESTAO.RELATORIO.ASSUNTOS') }}</h3>
              <p class="m-0 text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.RELATORIO.CATEGORIAS_DICA') }}</p>
              <div
                v-for="item in assuntos"
                :key="item.nome"
                class="grid grid-cols-[minmax(0,11rem)_1fr_auto] items-center gap-3 text-sm"
              >
                <span class="truncate text-n-slate-12" :title="item.nome">
                  {{ item.nome }}
                  <span v-if="item.novo" class="px-1.5 ms-1 text-xs rounded bg-n-amber-3 text-n-amber-11">
                    {{ t('NEUXS_GESTAO.RELATORIO.NOVO') }}
                  </span>
                </span>
                <span class="h-2.5 overflow-hidden rounded bg-n-alpha-2">
                  <span class="block h-full bg-n-blue-9" :style="{ width: `${(100 * item.total) / maior(assuntos)}%` }" />
                </span>
                <span class="text-xs tabular-nums text-n-slate-11">{{ item.total }}</span>
              </div>
            </div>
            <div v-if="motivos.length" class="flex flex-col gap-2">
              <h3 class="m-0 text-heading-3 text-n-blue-11">{{ t('NEUXS_GESTAO.RELATORIO.MOTIVOS') }}</h3>
              <div
                v-for="item in motivos"
                :key="item.nome"
                class="grid grid-cols-[minmax(0,11rem)_1fr_auto] items-center gap-3 text-sm"
              >
                <span class="truncate text-n-slate-12" :title="item.nome">
                  {{ item.nome }}
                  <span v-if="item.novo" class="px-1.5 ms-1 text-xs rounded bg-n-amber-3 text-n-amber-11">
                    {{ t('NEUXS_GESTAO.RELATORIO.NOVO') }}
                  </span>
                </span>
                <span class="flex h-2.5 overflow-hidden rounded bg-n-alpha-2">
                  <span class="bg-n-teal-9" :style="{ width: `${(100 * item.retidos) / maior(motivos)}%` }" />
                  <span class="bg-n-ruby-9 opacity-70" :style="{ width: `${(100 * (item.total - item.retidos)) / maior(motivos)}%` }" />
                </span>
                <span class="text-xs tabular-nums text-n-slate-11" :title="t('NEUXS_GESTAO.RELATORIO.MOTIVOS_DICA', { retidos: item.retidos, total: item.total })">
                  {{ item.retidos }}/{{ item.total }}
                </span>
              </div>
            </div>
          </section>

          <section v-for="grupo in destaques" :key="grupo.secao" class="flex flex-col gap-2">
            <h3 class="m-0 text-heading-3 text-n-blue-11">{{ t(`NEUXS_GESTAO.RELATORIO.SECOES.${grupo.secao}`) }}</h3>
            <ul class="flex flex-col gap-2 m-0 ps-5 text-sm text-n-slate-12 max-w-3xl">
              <li v-for="(item, i) in grupo.itens" :key="i">
                {{ item.texto }}
                <a
                  v-for="id in item.conversas"
                  :key="id"
                  :href="linkDaConversa(id)"
                  target="_blank"
                  rel="noopener"
                  class="ms-1 text-xs text-n-blue-11"
                >
                  {{ t('NEUXS_GESTAO.RELATORIO.CONVERSA', { id }) }}
                </a>
              </li>
            </ul>
          </section>

          <section v-if="relatorio.texto.recomendacoes?.length" class="flex flex-col gap-2 p-4 rounded-xl bg-n-alpha-1">
            <h3 class="m-0 text-heading-3 text-n-blue-11">{{ t('NEUXS_GESTAO.RELATORIO.RECOMENDACOES') }}</h3>
            <ol class="flex flex-col gap-2 m-0 ps-5 text-sm text-n-slate-12 max-w-3xl">
              <li v-for="(item, i) in relatorio.texto.recomendacoes" :key="i">
                {{ item.texto }}
                <a
                  v-for="id in item.conversas"
                  :key="id"
                  :href="linkDaConversa(id)"
                  target="_blank"
                  rel="noopener"
                  class="ms-1 text-xs text-n-blue-11"
                >
                  {{ t('NEUXS_GESTAO.RELATORIO.CONVERSA', { id }) }}
                </a>
              </li>
            </ol>
          </section>
        </div>
      </template>
    </article>
    <div
      v-if="compra"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black1"
      role="dialog"
      aria-modal="true"
      @click.self="compra = null"
    >
      <div class="flex flex-col w-full max-w-md gap-4 p-5 border shadow-xl rounded-xl border-n-weak bg-n-solid-1">
        <h2 class="m-0 text-heading-2 text-n-slate-12">{{ t('NEUXS_GESTAO.RELATORIO.COMPRA_TITULO') }}</h2>
        <p class="m-0 text-sm text-n-slate-11">
          {{
            t('NEUXS_GESTAO.RELATORIO.COMPRA_TEXTO', {
              inclusos: compra.inclusos,
              fecha: dataCurta(compra.fecha_em),
              preco: reais(compra.preco_extra),
            })
          }}
        </p>
        <p v-if="!compra.administrador" class="p-3 m-0 text-sm rounded-lg bg-n-amber-3 text-n-amber-11">
          {{ t('NEUXS_GESTAO.RELATORIO.COMPRA_SO_ADMIN') }}
        </p>
        <div class="flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="faded" color="slate" :label="t('NEUXS_GESTAO.RELATORIO.CANCELAR')" @click="compra = null" />
          <Button
            v-if="compra.administrador"
            size="sm"
            :label="t('NEUXS_GESTAO.RELATORIO.COMPRAR', { preco: reais(compra.preco_extra) })"
            :is-loading="pedindo"
            @click="gera(compra.dias, true)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
